import { ObjectPool, Service } from '@toeverything/infra';

import { PDF } from '../entities/pdf';

// One PDF document one worker.

// Idle retention: disposing a PDF terminates its PDFium worker, so the next
// mount pays wasm fetch + compile + document open again (>1s) — visible as a
// full reload when canvas navigation culls and re-mounts an embedded preview.
// Released PDFs therefore stay warm for a bounded window.
const PDF_IDLE_RETENTION_MS = 3 * 60 * 1000;
const PDF_IDLE_RETENTION_MAX = 3;

export class PDFService extends Service {
  // First time a PDF was seen with zero refs; cleared on re-acquire/delete.
  private readonly idleSince = new Map<PDF, number>();

  PDFs = new ObjectPool<string, PDF>({
    onDangling: pdf => this.shouldDisposeIdle(pdf),
    onDelete: pdf => {
      this.idleSince.delete(pdf);
      pdf.dispose();
    },
  });

  constructor() {
    super();
    this.disposables.push(() => {
      this.PDFs.clear();
    });
  }

  private shouldDisposeIdle(pdf: PDF) {
    const now = Date.now();
    let since = this.idleSince.get(pdf);
    if (since === undefined) {
      since = now;
      this.idleSince.set(pdf, since);
    }
    if (now - since > PDF_IDLE_RETENTION_MS) {
      return true;
    }
    if (this.idleSince.size > PDF_IDLE_RETENTION_MAX) {
      // Over the cap: dispose the oldest idle documents first.
      const oldestFirst = [...this.idleSince.entries()].sort(
        (a, b) => a[1] - b[1]
      );
      const overflow = oldestFirst.slice(
        0,
        this.idleSince.size - PDF_IDLE_RETENTION_MAX
      );
      return overflow.some(([candidate]) => candidate === pdf);
    }
    return false;
  }

  get(blobId: string) {
    let rc = this.PDFs.get(blobId);

    if (!rc) {
      rc = this.PDFs.put(blobId, this.framework.createEntity(PDF, { blobId }));
    }

    // Re-acquired — no longer idle.
    this.idleSince.delete(rc.obj);

    return { pdf: rc.obj, release: rc.release };
  }
}
